'use client';

import type { LibraryItem } from './schemas';

/**
 * Remove duplicates and empty strings from a group ID array.
 * Returns a stable, clean array.
 */
export function normalizeGroupIds(ids: string[] | undefined | null): string[] {
  if (!ids || !Array.isArray(ids)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of ids) {
    if (id && !seen.has(id)) {
      seen.add(id);
      result.push(id);
    }
  }
  return result;
}

/**
 * Return the primary group ID (first element) or null.
 */
export function getPrimaryGroupId(groupIds: string[]): string | null {
  return groupIds.length > 0 ? groupIds[0] : null;
}

/**
 * Ensure libraryGroupId matches the first element of detail.groupIds.
 * If detail.groupIds is empty, set libraryGroupId to null.
 */
export function syncPrimaryGroup(item: LibraryItem): LibraryItem {
  const groupIds = normalizeGroupIds(item.detail?.groupIds as string[] | undefined);
  return {
    ...item,
    libraryGroupId: getPrimaryGroupId(groupIds),
    detail: {
      ...item.detail,
      ...(groupIds.length > 0 ? { groupIds } : { groupIds: undefined }),
    },
  };
}

/**
 * Merge incoming group IDs into existing ones, deduplicate.
 */
export function mergeGroupIds(existing: string[] | undefined | null, incoming: string[]): string[] {
  const normalized = normalizeGroupIds(existing);
  const set = new Set(normalized);
  for (const id of incoming) {
    if (id) set.add(id);
  }
  return Array.from(set);
}

/**
 * Remove a single group from an item's groupIds.
 * Returns null if no groups remain (caller should delete the item).
 */
export function removeGroupId(item: LibraryItem, groupId: string): LibraryItem | null {
  const groupIds = normalizeGroupIds(item.detail?.groupIds as string[] | undefined).filter(
    (id) => id !== groupId
  );

  if (groupIds.length === 0) return null;

  const updated = {
    ...item,
    libraryGroupId: getPrimaryGroupId(groupIds),
    detail: {
      ...item.detail,
      groupIds,
    },
  };

  return updated;
}

/**
 * Determine whether an item matches the given identity.
 */
export function itemMatches(
  item: LibraryItem,
  type: string,
  regulationId: string,
  articleNumber?: string
): boolean {
  if (item.type !== type) return false;
  if (item.detail?.regulationId !== regulationId) return false;
  if (type === 'PASAL' && articleNumber !== undefined) {
    return item.detail?.articleNumber === articleNumber;
  }
  return true;
}

/**
 * Find an existing item matching the identity in a list.
 */
export function findMatchingItem(
  items: LibraryItem[],
  type: string,
  regulationId: string,
  articleNumber?: string
): LibraryItem | undefined {
  return items.find((item) => itemMatches(item, type, regulationId, articleNumber));
}

/**
 * Get the display-relevant group IDs from an item's detail and libraryGroupId.
 * Falls back to libraryGroupId if detail.groupIds is absent (legacy items).
 */
export function getDisplayGroupIds(item: LibraryItem): string[] {
  const detailGroupIds = item.detail?.groupIds as string[] | undefined;
  if (detailGroupIds && Array.isArray(detailGroupIds) && detailGroupIds.length > 0) {
    return normalizeGroupIds(detailGroupIds);
  }
  if (item.libraryGroupId) {
    return [item.libraryGroupId];
  }
  return [];
}

/**
 * Result of a save or update operation.
 */
export type SaveOrUpdateResult =
  | { action: 'created'; item: LibraryItem }
  | { action: 'updated'; item: LibraryItem };
